import { Box, Button, Menu, MenuItem, styled } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { AppAvatar, brandclub_colors, hexToRgba, useStacklineTheme } from '@stackline/ui';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { FC, ReactNode, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import SelectRetailerDropdown from 'src/components/BeaconV2Components/Header/SelectRetailerDropdown';
import SearchInput from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { useBeaconHomeLink } from 'src/components/BeaconV2Components/hooks/useBeaconHomeLink';
import { BEACON_PRO_CENTER_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import DisputeStatusUpdater from 'src/components/ShortageDisputes/components/DisputeManagement/DisputeStatusUpdater';
import { BeaconLogo } from 'src/components/SvgIcons/SvgIcons';
import queryClient from 'src/queryClient';
import * as appOperations from 'src/store/modules/app/operations';
import { authOperations } from 'src/store/modules/auth';
import { onShortagesMode } from 'src/store/modules/user/selectors';
import { useAppSelector } from 'src/utils/Hooks';
import { useAppSubscribed } from 'src/utils/Hooks/reduxSelectors';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { trackLogOut } from 'src/utils/mixpanel';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { GlobalComponents } from 'src/components/BeaconV2Components/Header/GlobalComponents/GlobalComponents';
import { shutdown } from '@intercom/messenger-js-sdk';
import { amplifySignOut } from 'src/utils/auth';
import { NotificationBadge } from 'src/components/common/NotificationBadge';

const useStyles = makeStyles({
  header: {
    height: 60,
    backgroundColor: brandclub_colors.blueWhite,
    position: 'initial',
    top: 0,
    zIndex: 1000
  },
  centerDiv: {
    margin: '0  auto',
    width: BEACON_PRO_CENTER_WIDTH,
    boxSizing: 'border-box',
    height: 60,
    display: 'grid',
    gridTemplateColumns: `1fr 330px 1fr`,
    alignItems: 'center'
  },
  productLogo: {
    height: 30,
    width: 30,
    marginLeft: '-1px',
    '& :hover': {
      cursor: 'pointer'
    }
  },
  userOptionsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px'
  },
  companyName: {
    fontSize: '1.4rem',
    fontWeight: 500,
    lineHeight: 1.43,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  settingsIconButton: {
    height: 24,
    width: 24,
    padding: 0,
    margin: '0 26px 0 20px',
    borderRight: '1ps solid black'
  },
  accountIconButton: {
    height: 30,
    width: 30,
    padding: 0
  },
  accountIcon: {
    height: 36,
    width: 36
  },
  // drawer
  drawerButtonContainer: {
    textAlign: 'right'
  },
  drawerButtonRoot: {
    paddingRight: 0
  },
  menuIcon: {
    transform: 'scale(1.4, 1.7)'
  },
  menuCloseIcon: {
    transform: 'scale(1.5)'
  },
  drawerRoot: {
    fontFamily: 'Roboto'
  },
  drawerPaperRoot: {
    top: 60,
    left: 0,
    padding: 20
  },
  drawerCompanyName: {
    color: brandclub_colors.darkBlue,
    fontSize: '1.8rem',
    fontWeight: 'bold',
    lineHeight: 1.43,
    marginTop: 10,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  listRoot: {
    color: brandclub_colors.darkBlue
  },
  listItemRoot: {
    padding: '20px 0',
    '& a': {
      color: brandclub_colors.darkBlue,
      textDecoration: 'none'
    }
  }
});

// -------------------------------------------------------------

export const SettingIcon = () => {
  return <div style={{ margin: '0 26px 0 20px' }}>settings</div>;
};

interface ButtonOption {
  label: string | ReactNode;
  id: string;
  onClick: () => void;
}

export const UserIcon = ({ history }: { history: any }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const containerRef = useRef(null);
  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  const dispatch = useDispatch();
  const theme = useStacklineTheme();

  const { email, isAdmin } = useAppSelector((state) => state.user.session);
  const shouldShowNotificationBadge = useNotificationsBadge(
    (state) =>
      state.notificationBadges.presentations || state.notificationBadges.export || state.notificationBadges.adjustments
  );
  const hoverBgColor = hexToRgba(theme.colors.secondaryPorcelain, 1);
  const selectedBackground = hexToRgba(theme.colors.primaryLight, 0.3);

  const role = isAdmin ? 'Admin' : 'User';

  const handleOpen = () => {
    setAnchorEl(containerRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChange = (selection) => {
    selection.onClick();
  };

  const buttonOptions: ButtonOption[] = [
    {
      label: (
        <div>
          Settings
          {shouldShowNotificationBadge && <NotificationBadge variant="small" />}
        </div>
      ),
      id: 'settings',
      onClick: () => {
        handleClose();
        if (!window.location.pathname.includes('account')) {
          history.push(`/account/profile${searchParams}`);
        }
      }
    },
    {
      label: 'Sign out',
      id: 'signOut',
      onClick: () => {
        history.push('/sign_out');
      }
    }
  ];

  return (
    <>
      <div style={{ height: 30, width: 30, cursor: 'pointer' }} role="button" onClick={handleOpen} ref={containerRef}>
        <AppProfileIcon shouldShowNotificationBadge={shouldShowNotificationBadge} size={30} />
      </div>
      <Menu
        autoFocus={false}
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
        open={!!anchorEl}
        onClose={handleClose}
        sx={{
          '& ul': {
            paddingY: 0
          },
          '.MuiPaper-root': {
            minWidth: '209px', // instead of width to allow for long emails
            overflowY: 'clip',
            padding: 0,
            marginTop: '8px'
          }
        }}
      >
        <MenuItem
          disabled
          sx={{
            paddingY: '14px',
            paddingX: '12px',
            borderBottom: `1px solid ${theme.colors.primaryGray}`,
            width: '100%',
            '.MuiMenuItem-root': {
              paddingLeft: '11px'
            },
            '&.Mui-disabled': {
              opacity: 1
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}
          >
            <Text variant="subtitle2" fontWeight="bold">
              {email}
            </Text>
            <Text opacity={50} variant="body3">
              {role}
            </Text>
          </Box>
        </MenuItem>
        {buttonOptions.map((option, index) => {
          const onSettingsPage = window.location.pathname === `/account/profile` && option.id === 'settings';

          return (
            <MenuItem
              key={option.id}
              sx={{
                backgroundColor: onSettingsPage ? selectedBackground : undefined,
                paddingY: '14px',
                paddingX: '12px',
                '&:hover': {
                  backgroundColor: onSettingsPage ? selectedBackground : hoverBgColor
                },
                borderBottom: index !== buttonOptions.length - 1 ? `1px solid ${theme.colors.primaryGray}` : undefined,
                width: '100%',
                '.MuiMenuItem-root': {
                  paddingLeft: '11px'
                }
              }}
              onClick={() => {
                onChange(option);
                handleClose();
              }}
            >
              <Text variant="subtitle2" fontWeight={500}>
                {option.label}
              </Text>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

interface HeaderProps {
  location: any;
  history: any;
}

const Header: FC = ({ location, history }: HeaderProps) => {
  const classes = useStyles();
  const beaconHomeLink = useBeaconHomeLink();
  const app = useAppSelector((state) => state.app);

  const APP_DETAILS = useAppSubscribed();

  const appDropdownOption = Object.keys(APP_DETAILS)
    .map((appName) => {
      return {
        ...APP_DETAILS[appName],
        label: APP_DETAILS[appName].getLabel(),
        disabled: !APP_DETAILS[appName].shouldBeEnabled()
      };
    })
    .filter((option) => {
      return option.id !== 'omni' && option.id !== 'drive';
    });

  return (
    <div className={classes.header}>
      <div className={classes.centerDiv}>
        <Flex alignItems="center">
          <GlobalComponents />
          <AppAvatar
            variant="square"
            sx={{ backgroundColor: 'transparent' }}
            classes={{
              root: classes.productLogo
            }}
            imgProps={{
              style: {
                objectFit: 'contain'
              }
            }}
            onClick={() => {
              if (`${location.pathname + location.search}` !== beaconHomeLink) {
                history.push(beaconHomeLink);
              }
            }}
          >
            <BeaconLogo />
          </AppAvatar>

          <SlDropdownMenu
            options={appDropdownOption}
            defaultLabel="Beacon"
            selectedId="beacon"
            onChange={(selectedApp: SlDropdownMenuOption & { path: string }) => {
              if (selectedApp.id !== 'beacon') {
                window.open(selectedApp.path, '_blank');
              }
            }}
            buttonVariant="titleSmall"
            iconStyle={{ marginLeft: '8px' }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            menuStyles={{ width: '112px' }}
            slMenuProps={{ fixSelectedId: false }}
          />
        </Flex>

        <SearchInput />
        <div>
          <div className={classes.userOptionsDiv}>
            <SelectRetailerDropdown />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                  <UserIcon history={history} />
                  {onShortagesMode() && app.name === 'beacon' ? <DisputeStatusUpdater /> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(Header);
